<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useAssignmentStore } from '@/stores';
import { primaryTypeCode, primaryTypeLabel } from '@/utils/credentialTypes';
import { TypeTag } from '@/components/common';

export interface OverdueItem {
  // Assignment record ID — used only for the list :key, never for navigation.
  id: string | number;
  // Course/pathway ID — the target of the navigation link.
  learningObjectId: string | number;
  title: string;
  // The row's resolved type code (cle/cpe/iapp/ea/skills/microlearning/
  // simulation) or 'pathway', with the label that goes with it. Both empty
  // when the row can't be named, which hides the chip — never a stand-in
  // type. The label is resolved here rather than derived from the code so an
  // admin-created credential type keeps the backend's display name.
  type: string;
  typeLabel: string;
  itemType: 'course' | 'pathway';
  daysOverdue: number;
}

const assignmentStore = useAssignmentStore();

// Map Assignment → OverdueItem internally
const items = computed<OverdueItem[]>(() =>
  assignmentStore.assignments.map((a) => ({
    id: a.id,
    learningObjectId: a.learningObjectId,
    title: a.learningObjectTitle,
    // primaryTypeCode() already resolves 'pathway' from itemType, so this no
    // longer special-cases it. It used to read `courseType`, which the backend
    // stopped feeding in Phase 10f — so every overdue course read "CLE".
    type: primaryTypeCode(a) ?? '',
    typeLabel: primaryTypeLabel(a),
    itemType: a.itemType || 'course',
    daysOverdue: Math.max(0, Math.floor((Date.now() - new Date(a.dueDate).getTime()) / (1000 * 60 * 60 * 24))),
  }))
);

function getItemLink(item: OverdueItem): string {
  return item.itemType === 'pathway' ? `/pathway/${item.learningObjectId}` : `/course/${item.learningObjectId}`;
}

function getItemTypeLabel(item: OverdueItem): string {
  if (item.itemType === 'pathway') return 'Pathway';
  return item.typeLabel;
}

onMounted(() => {
  assignmentStore.fetchOverdueAssignments();
});
</script>

<template>
  <!-- Loading skeleton -->
  <div v-if="assignmentStore.loading" class="dashboard-section dashboard-section-full overdue-loading">
    <div class="section-header overdue-header">
      <div class="overdue-header-content">
        <div class="skeleton skeleton-icon"></div>
        <div class="skeleton skeleton-title"></div>
      </div>
    </div>
    <div class="overdue-items-list">
      <div v-for="n in 2" :key="n" class="overdue-item skeleton-item">
        <div class="overdue-item-info">
          <div class="skeleton skeleton-chip"></div>
          <div class="skeleton skeleton-text"></div>
        </div>
        <div class="overdue-item-meta">
          <div class="skeleton skeleton-badge"></div>
        </div>
      </div>
    </div>
  </div>

  <!-- Content -->
  <bb-alert v-else-if="items.length > 0" variant="warning" class="dashboard-section-full">
    <span class="bb-alert__title">Overdue Assignments</span>
    Complete them as soon as possible to stay on track.
    <!-- Rows live in their own wrapper so Vue patches inside it, not the bb-alert host (Lit relocates the host's children). -->
    <div class="overdue-items-list">
      <RouterLink
        v-for="item in items"
        :key="`${item.itemType}-${item.id}`"
        :to="getItemLink(item)"
        class="overdue-item"
      >
        <div class="overdue-item-info">
          <TypeTag v-if="getItemTypeLabel(item)" :code="item.type" size="xs" class="overdue-item-type">{{ getItemTypeLabel(item) }}</TypeTag>
          <span class="overdue-item-title">{{ item.title }}</span>
        </div>
        <div class="overdue-item-meta">
          <span class="bb-tag -size-xs -danger">{{ item.daysOverdue }} day{{ item.daysOverdue !== 1 ? 's' : '' }} overdue</span>
          <bb-icon name="chevron_right" decorative></bb-icon>
        </div>
      </RouterLink>
    </div>
    <div class="overdue-footer">
      <RouterLink to="/assignments" class="section-link">View all assignments</RouterLink>
    </div>
  </bb-alert>
</template>

<style scoped>
/* Skeleton loading styles */
.overdue-loading {
  opacity: 0.7;
}

.skeleton {
  background: linear-gradient(90deg, #e0e0e0 25%, #f0f0f0 50%, #e0e0e0 75%);
  background-size: 200% 100%;
  animation: skeleton-pulse 1.5s ease-in-out infinite;
  border-radius: 4px;
}

@keyframes skeleton-pulse {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.skeleton-icon {
  width: 24px;
  height: 24px;
  border-radius: 50%;
}

.skeleton-title {
  width: 180px;
  height: 24px;
}

.skeleton-item {
  pointer-events: none;
}

.skeleton-chip {
  width: 40px;
  height: 20px;
}

.skeleton-text {
  width: 200px;
  height: 16px;
}

.skeleton-badge {
  width: 100px;
  height: 20px;
}
</style>
